import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Loading } from '../../components/ui/Loading';
import { ErrorAlert } from '../../components/ui/ErrorAlert';
import { Empty } from '../../components/ui/Empty';

interface ContactMessage {
  id: string;
  phone_number: string;
  email: string | null;
  message: string;
  status: 'UNDER_REVIEW' | 'RESOLVED' | 'BLOCKED';
  created_at: string;
}

export const AdminContactMessages = () => {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadMessages = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('contact_messages')
        .select('id, phone_number, email, message, status, created_at')
        .order('created_at', { ascending: false });
      if (dbError) throw dbError;
      setMessages((data as any) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id: string, status: ContactMessage['status']) => {
    try {
      const { error: updateError } = await supabase
        .from('contact_messages' as any)
        .update({ status } as any)
        .eq('id', id);
      if (updateError) throw updateError;
      await loadMessages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  useEffect(() => {
    loadMessages();
  }, []);

  if (loading) return <Loading />;
  if (error) return <ErrorAlert message={error} onRetry={loadMessages} />;
  if (messages.length === 0) return <Empty title="No messages" message="There are no contact messages yet." />;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Contact Messages</h1>
        <p className="text-sm text-gray-600">Messages sent from the Contact Us page.</p>
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Phone</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Message</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {messages.map((msg) => (
              <tr key={msg.id}>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {new Date(msg.created_at).toLocaleDateString('en-IN')}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">{msg.phone_number}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{msg.email ?? '-'}</td>
                <td className="max-w-xs px-4 py-3 text-sm text-gray-700">{msg.message}</td>
                <td className="px-4 py-3 text-sm">
                  <select
                    value={msg.status}
                    onChange={(e) => handleStatusChange(msg.id, e.target.value as ContactMessage['status'])}
                    className="rounded border border-gray-300 px-2 py-1 text-xs"
                  >
                    <option value="UNDER_REVIEW">Under Review</option>
                    <option value="RESOLVED">Resolved</option>
                    <option value="BLOCKED">Blocked</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
